/**
 * 文档分析页面 - Tab 方式展示解析结果
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { collectSseText, documentApi, getErrorMessage, projectApi } from '../services/api';
import { CloudArrowUpIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { createDraftStorage } from '../utils/draftStorage';
import { useAuth } from '../hooks/useAuth';
import { ANALYSIS_TABS, AnalysisTypeKey } from '../types';

const STREAM_UPDATE_DELAY = 80;

interface DocumentAnalysisProps {
  fileContent: string;
  fileName: string;
  analysisResults: Record<string, string>;
  onFileUpload: (content: string, fileName: string) => void;
  onAnalysisComplete: (results: Record<string, string>) => void;
  projectId?: number;
  readOnly?: boolean;
}

const DocumentAnalysis: React.FC<DocumentAnalysisProps> = ({
  fileContent,
  fileName: savedFileName,
  analysisResults,
  onFileUpload,
  onAnalysisComplete,
  projectId,
  readOnly = false,
}) => {
  const { user } = useAuth();
  const draftStore = useMemo(() => createDraftStorage(user?.id ?? 0), [user?.id]);

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamFlushTimerRef = useRef<number | null>(null);
  const pendingStreamingRef = useRef<Record<string, string>>({});

  // 当前激活的 tab
  const [activeTab, setActiveTab] = useState<string>('overview');

  // 编辑模式：tab key -> boolean
  const [editingTabs, setEditingTabs] = useState<Record<string, boolean>>({});
  // 草稿内容：tab key -> text
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // 多标段：上传后从后端返回的标段列表，及其选中项（-1 表示整篇/全部标段）
  const [sections, setSections] = useState<Array<{ index: number; title: string; text: string }> | null>(null);
  const [selectedSection, setSelectedSection] = useState<number>(-1);

  // 解析步骤顺序
  const analysisSteps: AnalysisTypeKey[] = useMemo(
    () => ANALYSIS_TABS.map((t) => t.key),
    []
  );

  // 当前正在解析的步骤索引
  const [currentStepIdx, setCurrentStepIdx] = useState(-1);
  // 流式预览：tab key -> text
  const [streamingPreviews, setStreamingPreviews] = useState<Record<string, string>>({});

  // 同步 drafts 与非编辑态的最新结果
  useEffect(() => {
    const next: Record<string, string> = {};
    for (const tab of ANALYSIS_TABS) {
      if (!editingTabs[tab.key]) {
        next[tab.key] = analysisResults[tab.key] || '';
      }
    }
    if (Object.keys(next).length > 0) {
      setDrafts((prev) => ({ ...prev, ...next }));
    }
  }, [analysisResults, editingTabs]);

  useEffect(() => {
    return () => {
      if (streamFlushTimerRef.current !== null) {
        window.clearTimeout(streamFlushTimerRef.current);
      }
    };
  }, []);

  // 处理换行符
  const normalizeLineBreaks = (text: string) => {
    if (!text) return text;
    return text
      .replace(/\\n/g, '\n')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');
  };

  const flushStreamingPreview = () => {
    if (streamFlushTimerRef.current !== null) {
      window.clearTimeout(streamFlushTimerRef.current);
      streamFlushTimerRef.current = null;
    }
    setStreamingPreviews((prev) => {
      const next = { ...prev };
      for (const key of analysisSteps) {
        next[key] = normalizeLineBreaks(pendingStreamingRef.current[key] || '');
      }
      return next;
    });
  };

  const scheduleStreamingPreview = (step: string, fullText: string) => {
    pendingStreamingRef.current[step] = fullText;
    if (streamFlushTimerRef.current !== null) return;
    streamFlushTimerRef.current = window.setTimeout(() => {
      flushStreamingPreview();
    }, STREAM_UPDATE_DELAY);
  };

  const resetStreamingPreview = () => {
    pendingStreamingRef.current = {};
    flushStreamingPreview();
    setStreamingPreviews({});
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      setUploading(true);
      setMessage(null);
      const formData = new FormData();
      formData.append('file', file);
      if (projectId) {
        formData.append('project_id', String(projectId));
      }
      const response = await documentApi.uploadFileWithProject(file, projectId);
      if (response.data.success && response.data.file_content) {
        draftStore.clearAll();
        onFileUpload(response.data.file_content, file.name);
        setSections(response.data.sections || null);
        setSelectedSection(-1);
        setMessage({ type: 'success', text: response.data.message });
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '文件上传失败') });
    } finally {
      setUploading(false);
    }
  };

  const handleAnalysis = async () => {
    if (!fileContent) {
      setMessage({ type: 'error', text: '请先上传文档' });
      return;
    }

    // 若选择了具体标段，仅把该标段文本送入分析（后端会限定分析范围到该标段）
    const selectedSec =
      selectedSection >= 0 && sections ? sections[selectedSection] : undefined;
    const effectiveContent = selectedSec ? selectedSec.text : fileContent;
    const effectiveTitle = selectedSec ? selectedSec.title : null;

    try {
      setAnalyzing(true);
      setMessage(null);
      resetStreamingPreview();
      setEditingTabs({});
      setCurrentStepIdx(0);

      const results: Record<string, string> = {};

      for (let i = 0; i < analysisSteps.length; i++) {
        const key = analysisSteps[i];
        setCurrentStepIdx(i);
        // 自动切换到当前正在解析的 tab
        setActiveTab(key);

        const response = await documentApi.analyzeDocumentStream({
          file_content: effectiveContent,
          analysis_type: key,
          section_index: selectedSec ? selectedSection : null,
          section_title: effectiveTitle,
        });

        const resultText = await collectSseText(
          response,
          (fullText) => {
            scheduleStreamingPreview(key, fullText);
          },
          `${ANALYSIS_TABS[i].label}解析失败`
        );

        flushStreamingPreview();
        const finalText = normalizeLineBreaks(resultText);
        results[key] = finalText;

        // 实时更新到父组件，让用户能看到已完成的 tab
        onAnalysisComplete({ ...results });

        // 实时保存到数据库
        if (projectId) {
          projectApi.saveAnalysis(projectId, key, finalText).catch(() => {});
        }
      }

      onAnalysisComplete(results);
      setMessage({ type: 'success', text: '标书解析完成' });
      resetStreamingPreview();
      setCurrentStepIdx(-1);
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '标书解析失败') });
      resetStreamingPreview();
      setCurrentStepIdx(-1);
    } finally {
      setAnalyzing(false);
    }
  };

  // --- Tab 编辑/保存 ---
  const startEdit = (tabKey: string) => {
    setDrafts((prev) => ({
      ...prev,
      [tabKey]: analysisResults[tabKey] || '',
    }));
    setEditingTabs((prev) => ({ ...prev, [tabKey]: true }));
  };

  const cancelEdit = (tabKey: string) => {
    setDrafts((prev) => ({
      ...prev,
      [tabKey]: analysisResults[tabKey] || '',
    }));
    setEditingTabs((prev) => ({ ...prev, [tabKey]: false }));
  };

  const saveEdit = (tabKey: string) => {
    const newContent = drafts[tabKey] || '';
    const updated = { ...analysisResults, [tabKey]: newContent };
    onAnalysisComplete(updated);
    setEditingTabs((prev) => ({ ...prev, [tabKey]: false }));
    const tabLabel = ANALYSIS_TABS.find((t) => t.key === tabKey)?.label || tabKey;
    setMessage({ type: 'success', text: `「${tabLabel}」已保存` });
    if (projectId) {
      projectApi.saveAnalysis(projectId, tabKey, newContent).catch(() => {});
    }
  };

  const currentStepLabel =
    currentStepIdx >= 0 && currentStepIdx < analysisSteps.length
      ? ANALYSIS_TABS[currentStepIdx].label
      : '';

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* 文件上传区域 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">📄 文档上传</h2>

        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            readOnly
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : 'border-gray-300 hover:border-gray-400 cursor-pointer'
          }`}
          onClick={() => {
            if (!readOnly) fileInputRef.current?.click();
          }}
        >
          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <p className="text-lg text-gray-600">
              {readOnly
                ? '只读模式，无法上传文件'
                : uploadedFile ? uploadedFile.name : '点击选择文件或拖拽文件到这里'}
            </p>
            <p className="text-sm text-gray-500 mt-2">支持 PDF 和 Word 文档，最大 10MB</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.doc"
            onChange={handleFileSelect}
            className="hidden"
            disabled={readOnly}
          />
        </div>

        {uploading && (
          <div className="mt-4 text-center">
            <div className="inline-flex items-center px-4 py-2 text-sm text-blue-600">
              <div className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              正在上传和处理文件...
            </div>
          </div>
        )}
      </div>

      {/* 文档分析区域 */}
      {fileContent && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">🔍 文档分析</h2>
          {(uploadedFile?.name || savedFileName) && (
            <div className="mb-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-gray-500 flex items-center gap-1.5">
                  <DocumentIcon className="w-4 h-4" />
                  当前文档：{uploadedFile?.name || savedFileName}
                </p>
                {sections && sections.length >= 2 && (
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <span>分析范围：</span>
                    <select
                      value={selectedSection}
                      onChange={(e) => setSelectedSection(Number(e.target.value))}
                      disabled={analyzing || readOnly}
                      className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value={-1}>整篇（全部标段）</option>
                      {sections.map((s) => (
                        <option key={s.index} value={s.index}>
                          {s.title}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              {sections && sections.length >= 2 && (
                <p className="text-xs text-gray-400 mt-1">
                  该文档含多个标段/包件，可在右侧选择“分析范围”仅解析某一标段。
                </p>
              )}
            </div>
          )}

          {/* 解析按钮 */}
          <div className="flex justify-center mb-6">
            <button
              onClick={handleAnalysis}
              disabled={analyzing || readOnly}
              title={readOnly ? '只读模式，无法解析标书' : undefined}
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {analyzing ? (
                <>
                  <div className="animate-spin -ml-1 mr-3 h-5 w-5 text-white">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  正在分析{currentStepLabel ? `「${currentStepLabel}」` : '标书'}...
                  <span className="ml-2 text-xs opacity-75">
                    ({currentStepIdx + 1}/{analysisSteps.length})
                  </span>
                </>
              ) : (
                <>
                  <DocumentIcon className="w-5 h-5 mr-2" />
                  解析标书
                </>
              )}
            </button>
          </div>

          {/* 流式分析内容 - 当前步骤预览 */}
          {analyzing && currentStepIdx >= 0 && streamingPreviews[analysisSteps[currentStepIdx]] && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-sm font-medium text-blue-800 mb-3">
                正在分析「{currentStepLabel}」...
              </h4>
              <div className="bg-white p-3 rounded-lg border border-gray-200 max-h-64 overflow-y-auto shadow-sm">
                <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed text-blue-900 font-sans">
                  {streamingPreviews[analysisSteps[currentStepIdx]]}
                </pre>
              </div>
            </div>
          )}

          {/* 结果 Tabs */}
          <div>
            {/* Tab 导航 */}
            <div className="border-b border-gray-200 mb-4 overflow-x-auto">
              <nav className="-mb-px flex gap-0 min-w-max" role="tablist">
                {ANALYSIS_TABS.map((tab) => {
                  const isActive = activeTab === tab.key;
                  const hasContent = !!(analysisResults[tab.key] || '').trim();
                  return (
                    <button
                      key={tab.key}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveTab(tab.key)}
                      className={`whitespace-nowrap px-2.5 py-2 text-sm font-medium border-b-2 transition-colors ${
                        isActive
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                      {hasContent && (
                        <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-green-500" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab 内容 */}
            <div className="min-h-[200px]">
              {ANALYSIS_TABS.map((tab) => {
                if (activeTab !== tab.key) return null;
                const content = analysisResults[tab.key] || '';
                const isEditing = editingTabs[tab.key] || false;
                const draft = drafts[tab.key] ?? content;

                return (
                  <div key={tab.key} role="tabpanel">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-800">{tab.label}</h3>
                      {!analyzing && content && !readOnly && (
                        <button
                          type="button"
                          onClick={() => (isEditing ? cancelEdit(tab.key) : startEdit(tab.key))}
                          className={`text-sm font-medium transition-colors ${
                            isEditing
                              ? 'text-gray-500 hover:text-gray-700'
                              : 'text-blue-600 hover:text-blue-700'
                          }`}
                        >
                          {isEditing ? '取消编辑' : '编辑'}
                        </button>
                      )}
                    </div>

                    {isEditing ? (
                      <div className="space-y-3">
                        <textarea
                          value={draft}
                          onChange={(e) =>
                            setDrafts((prev) => ({ ...prev, [tab.key]: e.target.value }))
                          }
                          rows={16}
                          className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y font-mono text-sm"
                          placeholder={`${tab.label}将在这里显示...`}
                        />
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => cancelEdit(tab.key)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            取消
                          </button>
                          <button
                            type="button"
                            onClick={() => saveEdit(tab.key)}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                          >
                            保存
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full p-4 border border-gray-300 rounded-lg max-h-96 overflow-y-auto bg-white shadow-sm">
                        {content.trim() ? (
                          <MarkdownRenderer content={content} />
                        ) : (
                          <p className="text-gray-400 text-sm italic">
                            {analyzing
                              ? '解析中，请稍候...'
                              : `点击上方"解析标书"按钮，${tab.label}将在这里显示`}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 消息提示 */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.type === 'success'
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}
    </div>
  );
};

export default DocumentAnalysis;
